﻿import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Calculate as CalcIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Rule as RuleIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Stop as StopIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";

import { KauflandArticlesApi } from "../../../../services/api/multi/kaufland";
import { RepricerArticlesApi } from "../../../../services/api/multi/repricer";
import { formatPricingHistory } from "../../utils/pricingHistory";
import { OttoMarginRulesApi } from "../../../../services/api/multi/otto";
import {
  ArticleSalesStatsDialog,
  SalesStatsOpenButton,
} from "../../components/ArticleSalesStatsDialog";

// ── helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  active: "success",
  update_needed: "warning",
  error: "error",
  oos: "default",
  paused: "info",
  unknown: "default",
};

const fmt = (val, decimals = 2) =>
  val != null ? `${Number(val).toFixed(decimals)} €` : null;

const DetailRow = ({ label, value }) => (
  <Grid container sx={{ py: 0.4 }} alignItems="baseline">
    <Grid item xs={5}>
      <Typography variant="caption" color="text.secondary">
        {label}:
      </Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography variant="caption" fontWeight={500}>
        {value ?? "—"}
      </Typography>
    </Grid>
  </Grid>
);

function ArticleExpandedDetails({ article, onNavigateToBase }) {
  const kd = article.kaufland;
  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Typography
            variant="subtitle2"
            color="primary"
            gutterBottom
            onClick={() => onNavigateToBase && onNavigateToBase(article.sku)}
            sx={{ cursor: "pointer", textDecoration: "underline" }}
          >
            Base
          </Typography>
          <Divider sx={{ mb: 0.5 }} />
          <DetailRow label="Name" value={article.articlename} />
          <DetailRow
            label="Plenty Article ID"
            value={article.plenty_article_id}
          />
          <DetailRow
            label="Plenty Variant ID"
            value={article.plenty_variant_id}
          />
          <DetailRow label="Parent SKU" value={article.parent_sku} />
          <DetailRow label="Child SKUs" value={article.child_skus} />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow label="EANs" value={article.eans?.join(", ")} />
          <DetailRow label="Weight (g)" value={article.weight} />
          <DetailRow label="Package" value={article.package_type} />
          <DetailRow
            label="Package Cost"
            value={
              article.package_cost != null
                ? `${Number(article.package_cost).toFixed(2)} €`
                : null
            }
          />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow label="Buy Price" value={fmt(article.brutto_buy_price)} />
          <DetailRow label="Bundle Type" value={article.bundle_type} />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow
            label="Last Inbound Date"
            value={article.last_inbound_date}
          />
          <DetailRow label="Article Type" value={article.article_type} />
          <DetailRow
            label="Pricing History"
            value={formatPricingHistory(article.pricing_history)}
          />
          <DetailRow
            label="Ratios (Last 10)"
            value={
              article.ratios?.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {article.ratios.slice(0, 10).map((r, i) => (
                    <Chip
                      key={i}
                      label={typeof r === "number" ? r.toFixed(2) : r}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.6rem" }}
                    />
                  ))}
                </Stack>
              ) : null
            }
          />
        </Grid>
        {kd && (
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Kaufland Fees
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow label="VAT %" value={kd.vat} />
            <DetailRow label="Variable Fee %" value={kd.variable_fee} />
            <DetailRow label="Fixed Fee" value={fmt(kd.fixed_fee)} />
            <DetailRow label="Shipping Rate" value={fmt(kd.shipping_rate)} />
            <DetailRow label="Shipping Cat." value={kd.shipping_category} />
            <DetailRow label="Product ID" value={kd.id_product} />
            <DetailRow label="Unit ID" value={kd.id_unit} />
          </Grid>
        )}
        {kd && (
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Kaufland Config
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow
              label="Use Min/Max Rules"
              value={
                kd.use_min_max_rules != null
                  ? kd.use_min_max_rules
                    ? "Yes"
                    : "No"
                  : null
              }
            />
            <DetailRow label="Buybox Seller" value={kd.buybox_seller} />
            <DetailRow label="Buybox Price" value={fmt(kd.buybox_price)} />
            <DetailRow label="VAT Indicator" value={kd.vat_indicator} />
            <DetailRow label="Margin Rule ID" value={kd.margin_rule_id} />
            {kd.product_url && (
              <DetailRow
                label="Product URL"
                value={
                  <a
                    href={kd.product_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Open
                  </a>
                }
              />
            )}
            {kd.error_message && (
              <DetailRow
                label="Error"
                value={
                  <Typography variant="caption" color="error">
                    {kd.error_message}
                  </Typography>
                }
              />
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// ── Edit Kaufland details dialog ──────────────────────────────────────────────

const EMPTY_KD_FORM = {
  status: "active",
  price: "",
  minimum_price: "",
  maximum_price: "",
  storefront: "",
  margin_rule_id: "",
  use_min_max_rules: true,
};

const ARTICLE_STATUS_OPTIONS = [
  "active",
  "paused",
  "error",
  "update_needed",
  "oos",
  "unknown",
];

function EditKauflandDetailsDialog({ article, open, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_KD_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!article?.kaufland) return;
    const kd = article.kaufland;
    setForm({
      status: kd.status || "active",
      price: kd.price ?? "",
      minimum_price: kd.minimum_price ?? "",
      maximum_price: kd.maximum_price ?? "",
      storefront: kd.storefront ?? "",
      margin_rule_id: kd.margin_rule_id ?? "",
      use_min_max_rules: kd.use_min_max_rules ?? true,
    });
    setError(null);
  }, [article]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const kd = article.kaufland;
      const payload = {};
      if ((form.status || "active") !== (kd.status || "active"))
        payload.status = form.status || "active";
      if (String(form.price) !== String(kd.price ?? ""))
        payload.price = form.price !== "" ? Number(form.price) : null;
      if (String(form.minimum_price) !== String(kd.minimum_price ?? ""))
        payload.minimum_price =
          form.minimum_price !== "" ? Number(form.minimum_price) : null;
      if (String(form.maximum_price) !== String(kd.maximum_price ?? ""))
        payload.maximum_price =
          form.maximum_price !== "" ? Number(form.maximum_price) : null;
      if (form.storefront !== (kd.storefront ?? ""))
        payload.storefront = form.storefront || null;
      if (String(form.margin_rule_id) !== String(kd.margin_rule_id ?? ""))
        payload.margin_rule_id =
          form.margin_rule_id !== "" ? Number(form.margin_rule_id) : null;
      if (form.use_min_max_rules !== (kd.use_min_max_rules ?? true))
        payload.use_min_max_rules = form.use_min_max_rules;
      await RepricerArticlesApi.updateKauflandDetails(article.sku, payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const f = (label, field, type = "text") => (
    <TextField
      label={label}
      value={form[field]}
      onChange={(e) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }))
      }
      type={type}
      size="small"
      fullWidth
      inputProps={{ step: "any" }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Kaufland Details: {article?.sku}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status || "active"}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                {ARTICLE_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            {f("Price (€)", "price", "number")}
          </Grid>
          <Grid item xs={6}>
            {f("Min Price (€)", "minimum_price", "number")}
          </Grid>
          <Grid item xs={6}>
            {f("Max Price (€)", "maximum_price", "number")}
          </Grid>
          <Grid item xs={6}>
            {f("Storefront", "storefront")}
          </Grid>
          <Grid item xs={6}>
            {f("Margin Rule ID", "margin_rule_id", "number")}
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.use_min_max_rules}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      use_min_max_rules: e.target.checked,
                    }))
                  }
                />
              }
              label="Use Min/Max Rules"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete Kaufland details dialog ────────────────────────────────────────────

function DeleteKauflandDetailsDialog({ sku, open, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await RepricerArticlesApi.deleteKauflandDetails(sku);
      onDeleted();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove Kaufland Details</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Typography>
          Remove Kaufland details for <strong>{sku}</strong>? The base article
          will remain.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function StatusChip({ status }) {
  return (
    <Chip
      label={status || "—"}
      color={STATUS_COLORS[status] || "default"}
      size="small"
      variant="outlined"
    />
  );
}

function StatsBar({ stats }) {
  if (!stats) return null;
  const items = [
    { label: "Total", value: stats.total_count, color: "primary" },
    { label: "Stock", value: stats.total_stock, color: "success" },
    ...(stats.by_status
      ? Object.entries(stats.by_status).map(([s, v]) => ({
          label: s,
          value: v.count,
          color: STATUS_COLORS[s] || "default",
        }))
      : []),
  ];
  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 2 }}
    >
      {items.map((i) => (
        <Chip
          key={i.label}
          label={`${i.label}: ${i.value}`}
          color={i.color}
          size="small"
        />
      ))}
    </Stack>
  );
}

// ── Margin rules dialog ────────────────────────────────────────────────────────

function MarginRuleDialog({ article, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rule, setRule] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !article) {
      setRule(null);
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const fetchRule = async () => {
      try {
        const id = article.kaufland?.margin_rule_id;
        if (id != null) {
          // Kaufland uses Otto's margin rules api natively
          const res = await OttoMarginRulesApi.getMarginRule(id);
          if (active) setRule(res.data);
        } else {
          if (active) setRule(null);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchRule();

    return () => {
      active = false;
    };
  }, [open, article]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Kaufland Margin Rule: {article?.sku}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ color: "#e10915", mb: 1 }}>
              Margin Rule ID: {article?.kaufland?.margin_rule_id || "None"}
            </Typography>
            {!rule ? (
              <Typography variant="caption" color="text.secondary">
                Not found or not configured
              </Typography>
            ) : (
              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Buy Price (up to):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.buy_price)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Min Margin:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.min_margin} %
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Min Profit (Min):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.min_min_profit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Max Margin:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.max_margin} %
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Max Profit (Min):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.max_min_profit)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Cron dialog ───────────────────────────────────────────────────────────────

function CronDialog({ open, value, onChange, onConfirm, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set Kaufland Cron Schedule</DialogTitle>
      <DialogContent>
        <Cron value={value} showResultText onChange={onChange} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm}>
          Save & Start
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const STOREFRONTS = ["de", "at", "sk", "pl", "cz", "be", "nl"];

export default function KauflandArticlesSubTab({
  initialSku = null,
  onSkuConsumed,
  onNavigateToBase,
} = {}) {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [salesStatsSku, setSalesStatsSku] = useState(null);

  // search / filter
  const [skuSearch, setSkuSearch] = useState(initialSku || "");
  const [activeSkuSearch, setActiveSkuSearch] = useState(initialSku || "");
  const [statusFilter, setStatusFilter] = useState("ALL");
  // advanced filters & sort
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [activeAdvancedFilters, setActiveAdvancedFilters] = useState([]);
  const [sortOrder, setSortOrder] = useState({ field: null, dir: null });

  const [storefrontFilter, setStorefrontFilter] = useState("ALL");
  const [searchTrigger, setSearchTrigger] = useState(0);

  // consume initialSku navigation
  useEffect(() => {
    if (initialSku) {
      setSkuSearch(initialSku);
      setActiveSkuSearch(initialSku);
      setPage(0);
      if (onSkuConsumed) onSkuConsumed();
    }
  }, [initialSku, onSkuConsumed]);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // edit/delete details dialogs
  const [editDetailsArticle, setEditDetailsArticle] = useState(null);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [deleteDetailsSku, setDeleteDetailsSku] = useState(null);
  const [deleteDetailsOpen, setDeleteDetailsOpen] = useState(false);
  const [marginRuleArticle, setMarginRuleArticle] = useState(null);
  const [marginRuleOpen, setMarginRuleOpen] = useState(false);

  // per-row action loading
  const [recalcLoading, setRecalcLoading] = useState({});
  const [sendLoading, setSendLoading] = useState({});
  const [syncRowLoading, setSyncRowLoading] = useState({});
  const [syncing, setSyncing] = useState(false);

  const toggleRow = (sku) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  // cron
  const [cronStatus, setCronStatus] = useState({
    active: false,
    expression: "0 8 * * *",
  });
  const [cronDialogOpen, setCronDialogOpen] = useState(false);
  const [cronExpr, setCronExpr] = useState("0 8 * * *");
  const [cronLoading, setCronLoading] = useState(false);

  const buildFilters = useCallback(() => {
    // always restrict to articles with kaufland details
    const f = [{ field: "kaufland.article_id", op: "is_not_null" }];
    if (activeSkuSearch)
      f.push({ field: "sku", op: "like", value: `%${activeSkuSearch}%` });
    if (statusFilter !== "ALL")
      f.push({ field: "kaufland.status", op: "eq", value: statusFilter });
    if (storefrontFilter !== "ALL")
      f.push({
        field: "kaufland.storefront",
        op: "eq",
        value: storefrontFilter,
      });

    // advanced filters
    for (const af of activeAdvancedFilters) {
      if (af.field && af.op && af.value !== "") {
        f.push({ field: af.field, op: af.op, value: af.value });
      }
    }
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSkuSearch,
    statusFilter,
    storefrontFilter,
    searchTrigger,
    activeAdvancedFilters,
  ]);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = buildFilters();
      const orders = sortOrder.field
        ? [{ field: sortOrder.field, dir: sortOrder.dir }]
        : undefined;
      const res = await RepricerArticlesApi.getArticles({
        details: "kaufland",
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        filters,
        orders,
      });
      setArticles(res.data || []);
      setTotal(res.data?.length ?? 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [buildFilters, page, rowsPerPage, sortOrder]);

  const loadStats = useCallback(async () => {
    try {
      const res = await RepricerArticlesApi.getArticleStats("kaufland");
      setStats(res.data);
    } catch {
      /* silent */
    }
  }, []);

  const loadCronStatus = useCallback(async () => {
    try {
      const res = await KauflandArticlesApi.getCronStatus();
      setCronStatus(res.data);
      if (res.data.expression) setCronExpr(res.data.expression);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);
  useEffect(() => {
    loadStats();
    loadCronStatus();
  }, [loadStats, loadCronStatus]);

  const handleSearch = () => {
    setPage(0);
    setActiveSkuSearch(skuSearch);
    setSearchTrigger((prev) => prev + 1);
    setActiveAdvancedFilters([...advancedFilters]);
  };
  const handleClearSearch = () => {
    setSkuSearch("");
    setActiveSkuSearch("");
    setPage(0);
    setSearchTrigger((prev) => prev + 1);
  };
  const handleSort = (field) => {
    setSortOrder((prev) => {
      const isAsc = prev.field === field && prev.dir === "asc";
      const isDesc = prev.field === field && prev.dir === "desc";
      if (isAsc) return { field, dir: "desc" };
      if (isDesc) return { field: null, dir: null };
      return { field, dir: "asc" };
    });
    setPage(0);
  };

  const handleAddAdvancedFilter = () => {
    setAdvancedFilters((prev) => [
      ...prev,
      { id: Date.now(), field: "brutto_buy_price", op: "eq", value: "" },
    ]);
  };

  const handleRemoveAdvancedFilter = (id) => {
    setAdvancedFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpdateAdvancedFilter = (id, key, val) => {
    setAdvancedFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: val } : f)),
    );
  };
  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const sf = storefrontFilter !== "ALL" ? storefrontFilter : "de";
      await KauflandArticlesApi.syncArticles(sf);
      setSuccess("Sync triggered");
      await loadArticles();
      await loadStats();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncArticle = async (sku) => {
    setSyncRowLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      const sf = storefrontFilter !== "ALL" ? storefrontFilter : "de";
      await KauflandArticlesApi.syncArticles(sf, sku);
      setSuccess(`Synced from Kaufland: ${sku}`);
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncRowLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleRecalculateAll = async () => {
    setSyncing(true);
    setError(null);
    try {
      await KauflandArticlesApi.recalculate();
      setSuccess("Recalculate all articles triggered");
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleRecalculate = async (sku) => {
    setRecalcLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await KauflandArticlesApi.recalculate(sku);
      setSuccess(`Recalculated: ${sku}`);
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setRecalcLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleSendAllMinPrices = async () => {
    setSyncing(true);
    setError(null);
    try {
      const sf = storefrontFilter !== "ALL" ? storefrontFilter : undefined;
      await KauflandArticlesApi.sendMinimumPrices("all", sf);
      setSuccess("Sent all minimum prices");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSendMinPrices = async (sku) => {
    setSendLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      const sf = storefrontFilter !== "ALL" ? storefrontFilter : undefined;
      await KauflandArticlesApi.sendMinimumPrices(sku, sf);
      setSuccess(`Min prices sent: ${sku}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSendLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleStartCron = async () => {
    setCronLoading(true);
    try {
      await KauflandArticlesApi.startCronJob(cronExpr);
      setSuccess(`Cron started: ${cronExpr}`);
      setCronDialogOpen(false);
      await loadCronStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCronLoading(false);
    }
  };

  const handleStopCron = async () => {
    setCronLoading(true);
    try {
      await KauflandArticlesApi.stopCronJob();
      setSuccess("Cron stopped");
      await loadCronStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCronLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Stats */}
          <StatsBar stats={stats} />

          {/* Action bar */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={
                syncing ? <CircularProgress size={16} /> : <SyncIcon />
              }
              onClick={handleSync}
              disabled={syncing}
              size="small"
            >
              Sync from Kaufland
            </Button>

            <Button
              variant="outlined"
              startIcon={
                syncing ? <CircularProgress size={16} /> : <CalcIcon />
              }
              onClick={handleRecalculateAll}
              disabled={syncing}
              size="small"
            >
              Recalculate All
            </Button>

            <Button
              variant="outlined"
              startIcon={
                syncing ? <CircularProgress size={16} /> : <SendIcon />
              }
              onClick={handleSendAllMinPrices}
              disabled={syncing}
              size="small"
            >
              Send All Min Prices
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadArticles();
                loadStats();
              }}
              size="small"
            >
              Refresh
            </Button>

            <Divider orientation="vertical" flexItem />

            {/* Cron controls */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                size="small"
                label={
                  cronStatus.active
                    ? `Cron: ON (${cronStatus.expression || ""})`
                    : "Cron: OFF"
                }
                color={cronStatus.active ? "success" : "default"}
                variant="outlined"
              />
              {cronStatus.active ? (
                <Tooltip title="Stop cron">
                  <IconButton
                    size="small"
                    onClick={handleStopCron}
                    disabled={cronLoading}
                    color="error"
                  >
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Configure & start cron">
                  <IconButton
                    size="small"
                    onClick={() => setCronDialogOpen(true)}
                    color="success"
                  >
                    <PlayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {cronStatus.next_run_at && (
              <Typography variant="caption" color="text.secondary">
                Next: {new Date(cronStatus.next_run_at).toLocaleString()}
              </Typography>
            )}
          </Stack>

          {/* Filter bar */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search SKU…"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: skuSearch ? (
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIcon fontSize="small" />
                ),
              }}
              sx={{ width: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                {["ACTIVE", "UPDATE_NEEDED", "ERROR", "OOS", "PAUSED"].map(
                  (s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Storefront</InputLabel>
              <Select
                value={storefrontFilter}
                label="Storefront"
                onChange={(e) => {
                  setStorefrontFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                {STOREFRONTS.map((sf) => (
                  <MenuItem key={sf} value={sf}>
                    {sf.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>

            <Button
              variant={advancedFiltersOpen ? "contained" : "outlined"}
              size="small"
              onClick={() => setAdvancedFiltersOpen((prev) => !prev)}
              startIcon={<FilterListIcon />}
            >
              Filters ({advancedFilters.length})
            </Button>
            {(activeSkuSearch ||
              statusFilter !== "ALL" ||
              activeAdvancedFilters.length > 0) && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearSearch}
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Table */}

          <Collapse in={advancedFiltersOpen} unmountOnExit>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="subtitle2">Advanced Filters</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddAdvancedFilter}
                >
                  Add Rule
                </Button>
              </Stack>
              {advancedFilters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No advanced filters active.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {advancedFilters.map((f) => (
                    <Stack
                      key={f.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          value={f.field}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "field",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="brutto_buy_price">
                            Buy Price
                          </MenuItem>
                          <MenuItem value="stock">Stock</MenuItem>
                          <MenuItem value="package_type">Package Type</MenuItem>
                          <MenuItem value="weight">Weight</MenuItem>
                          <MenuItem value="kaufland.buybox_price">
                            Buybox Price
                          </MenuItem>
                          <MenuItem value="kaufland.expected_profit">
                            Expected Profit
                          </MenuItem>
                          <MenuItem value="kaufland.use_min_max_rules">
                            Use Min/Max Rules
                          </MenuItem>
                          <MenuItem value="kaufland.price">Price</MenuItem>
                          <MenuItem value="kaufland.listing_price">
                            Listing Price
                          </MenuItem>
                          <MenuItem value="kaufland.minimum_price">
                            Min Price
                          </MenuItem>
                          <MenuItem value="kaufland.maximum_price">
                            Max Price
                          </MenuItem>
                          <MenuItem value="kaufland.is_buybox">
                            Is Buybox
                          </MenuItem>
                          <MenuItem value="kaufland.buybox_seller">
                            Buybox Seller
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={f.op}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "op",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="eq">Equals (=)</MenuItem>
                          <MenuItem value="gt">Greater (&gt;)</MenuItem>
                          <MenuItem value="gte">Greater eq (&gt;=)</MenuItem>
                          <MenuItem value="lt">Less (&lt;)</MenuItem>
                          <MenuItem value="lte">Less eq (&lt;=)</MenuItem>
                          <MenuItem value="like">Contains</MenuItem>
                          <MenuItem value="is_null">Is null/none</MenuItem>
                          <MenuItem value="is_not_null">Is not null</MenuItem>
                        </Select>
                      </FormControl>
                      {f.op !== "is_null" && f.op !== "is_not_null" && (
                        <TextField
                          size="small"
                          placeholder="Value"
                          value={f.value}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "value",
                              e.target.value,
                            )
                          }
                          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          sx={{ width: 140 }}
                        />
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAdvancedFilter(f.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearch}
                      sx={{ mt: 1 }}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Collapse>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "sku"}
                      direction={
                        sortOrder.field === "sku" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("sku")}
                    >
                      SKU
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortOrder.field === "kaufland.price"}
                      direction={
                        sortOrder.field === "kaufland.price"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("kaufland.price")}
                    >
                      Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Listing Price</TableCell>
                  <TableCell align="right">Min Price</TableCell>
                  <TableCell align="right">Max Price</TableCell>
                  <TableCell align="right">Exp. Profit</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "kaufland.status"}
                      direction={
                        sortOrder.field === "kaufland.status"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("kaufland.status")}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Buybox</TableCell>
                  <TableCell>Storefront</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortOrder.field === "stock"}
                      direction={
                        sortOrder.field === "stock" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("stock")}
                    >
                      Stock
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : articles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No articles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  articles.map((a) => {
                    const kd = a.kaufland;
                    const expanded = expandedRows.has(a.sku);
                    return (
                      <React.Fragment key={a.sku}>
                        <TableRow
                          hover
                          sx={{
                            cursor: "pointer",
                            "& > td": expanded ? { borderBottom: "none" } : {},
                          }}
                          onClick={() => toggleRow(a.sku)}
                        >
                          <TableCell padding="checkbox">
                            <IconButton size="small">
                              {expanded ? (
                                <CollapseIcon fontSize="small" />
                              ) : (
                                <ExpandIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            {kd?.product_url ? (
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                component="a"
                                href={kd.product_url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  textDecoration: "underline",
                                  color: "primary.main",
                                }}
                              >
                                {a.sku}
                              </Typography>
                            ) : (
                              <Typography variant="body2" fontWeight={500}>
                                {a.sku}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(kd?.price) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(kd?.listing_price) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(kd?.minimum_price) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(kd?.maximum_price) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(kd?.expected_profit) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={kd?.status} />
                          </TableCell>
                          <TableCell>
                            {kd?.is_buybox != null ? (
                              <Chip
                                label={kd.is_buybox ? "Yes" : "No"}
                                color={kd.is_buybox ? "success" : "default"}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {kd?.storefront?.toUpperCase() || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {a.stock ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}
                            >
                              {kd?.updated_at
                                ? new Date(kd.updated_at).toLocaleString()
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <SalesStatsOpenButton
                                onClick={() => setSalesStatsSku(a.sku)}
                              />
                              <Tooltip title="Edit details">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditDetailsArticle(a);
                                    setEditDetailsOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sync article">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handleSyncArticle(a.sku)}
                                    disabled={!!syncRowLoading[a.sku]}
                                  >
                                    {syncRowLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <SyncIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Recalculate">
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "purple" }}
                                    onClick={() => handleRecalculate(a.sku)}
                                    disabled={!!recalcLoading[a.sku]}
                                  >
                                    {recalcLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <CalcIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Send min prices">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSendMinPrices(a.sku)}
                                    disabled={!!sendLoading[a.sku]}
                                  >
                                    {sendLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <SendIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="View Margin Rules">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => {
                                    setMarginRuleArticle(a);
                                    setMarginRuleOpen(true);
                                  }}
                                >
                                  <RuleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove Kaufland details">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setDeleteDetailsSku(a.sku);
                                    setDeleteDetailsOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {/* Expanded details row */}
                        <TableRow>
                          <TableCell
                            colSpan={13}
                            sx={{ p: 0, border: expanded ? undefined : "none" }}
                          >
                            <Collapse
                              in={expanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <ArticleExpandedDetails
                                article={a}
                                onNavigateToBase={onNavigateToBase}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={stats?.total_count ?? total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </CardContent>
      </Card>

      <EditKauflandDetailsDialog
        article={editDetailsArticle}
        open={editDetailsOpen}
        onClose={() => setEditDetailsOpen(false)}
        onSaved={() => loadArticles()}
      />

      <DeleteKauflandDetailsDialog
        sku={deleteDetailsSku}
        open={deleteDetailsOpen}
        onClose={() => setDeleteDetailsOpen(false)}
        onDeleted={() => {
          loadArticles();
          setSuccess(`Kaufland details removed: ${deleteDetailsSku}`);
        }}
      />

      <MarginRuleDialog
        article={marginRuleArticle}
        open={marginRuleOpen}
        onClose={() => setMarginRuleOpen(false)}
      />

      <CronDialog
        open={cronDialogOpen}
        value={cronExpr}
        onChange={(v) => setCronExpr(v)}
        onConfirm={handleStartCron}
        onClose={() => setCronDialogOpen(false)}
      />

      <ArticleSalesStatsDialog
        open={!!salesStatsSku}
        onClose={() => setSalesStatsSku(null)}
        sku={salesStatsSku}
        articlename={articles.find((x) => x.sku === salesStatsSku)?.articlename}
        channel="kaufland"
      />
    </Box>
  );
}
